import { Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../../../../../../_augmentt/selectors/customers'
import { ModalPanelSwitch, ModalSidebar } from '../../../../../components/ModalSidebar'
import useAllEmployees from '../../../../../hooks/useAllEmployees'
import useEngageOffboarding from '../../../../../hooks/useEngageOffboarding'
import useEngageTaskScheduling from '../../../../../hooks/useEngageTaskScheduling'
import useUserInfo from '../../../../../hooks/useUserInfo'
import { getEmployeeFullName } from '../../../../../util/table.util'
import { DEFAULT_SETTINGS_ACTIONS } from '../../constants'
import useDefaultSettings from '../../default-settings/useDefaultSettings'
import { SCHEDULER_ACTIONS } from '../../scheduler.utils'
import { isScheduleSelected, toApiPayload as toScheduleApiPayload } from '../common/Scheduling/fieldset'
import { MODAL_STEPS, STEP_KEYS } from './constants'
import DeleteUserPanel from './DeleteUserPanel'
import OptionalSettingsPanel from './OptionalSettingsPanel'
import ReviewPanel from './ReviewPanel'
import SubmitPanel from './SubmitPanel'
import useFormOptions, { FormOptionsContext } from './useFormOptions'
import { buildInitialValues, getValidationSchema, toApiPayload } from './utils'

const propTypes = {
  /**
   * If true, modal is shown.
   */
  show: PropTypes.bool,
  /**
   * Callback to hide the modal
   */
  onHide: PropTypes.func.isRequired,
  /**
   * If true, puts modal behind the backdrop, in case of another modal is opened on top of it for instance
   */
  toBackdrop: PropTypes.bool,
  /**
   * Employee to be offboarded
   */
  employee: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  /**
   * Customer employees
   */
  allEmployees: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to update the employee list
   */
  onUpdate: PropTypes.func.isRequired,
  /**
   * Indicates if employee has a task scheduled for this action already
   */
  scheduledTask: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Api call to fetch all upcoming scheduled tasks for this customer
   */
  fetchUpcomingEngageTasks: PropTypes.func.isRequired,
}

function OffboardModal(props) {
  const {
    show = false,
    onHide,
    toBackdrop = false,
    employee,
    allEmployees = [],
    onUpdate,
    scheduledTask = null,
    fetchUpcomingEngageTasks,
  } = props ?? {}

  const employeeFullName = getEmployeeFullName(employee)

  // previous offboard modal has this logic in place where if 'isUserMailBoxAvailable' is undefined we still show the mailbox options
  // apparently it can come as undefined sometimes, opened a bug for that: AP-7937
  const isUserMailBoxAvailable = employee?.isUserMailBoxAvailable !== false

  const intl = useIntl()

  const organizationId = useSelector(({ user }) => user?.data?.organization)

  const customerId = useSelector(getSelectedCustomerId)

  const userId = useSelector(({ user }) => user?.data?.id)

  // Use centralized hook for employee fetching
  const { allEmployees: allEmployeesFinal } = useAllEmployees({
    allEmployees,
    customerId,
    show,
  })

  const formOptions = useFormOptions({ allEmployees: allEmployeesFinal, employee })
  const {
    employeeEmailOptions,
    m365GroupOptions,
    securityGroupOptions,
    distributionGroupOptions,
    sharedMailboxOptions,
  } = formOptions

  const { userInfo, isFetchingUserInfo } = useUserInfo({ autoFetch: true, employeeId: employee.id })

  const defaultSettings = useDefaultSettings({ action: DEFAULT_SETTINGS_ACTIONS.OFFBOARDING })

  const initialValues = useMemo(
    () =>
      buildInitialValues({
        userInfo,
        isUserMailBoxAvailable,
        defaultSettings,
        m365GroupOptions,
        securityGroupOptions,
        distributionGroupOptions,
        sharedMailboxOptions,
      }),
    [
      userInfo,
      isUserMailBoxAvailable,
      defaultSettings,
      m365GroupOptions,
      securityGroupOptions,
      distributionGroupOptions,
      sharedMailboxOptions,
    ],
  )

  const validationSchema = useMemo(() => getValidationSchema(intl), [intl])

  const { offboardUser, errorOffboardingUser, offboardingUserData, successOffboardingUser, resetOffboardUserControls } =
    useEngageOffboarding()

  const { scheduleEngageTask, errorSchedulingEngageTask, successSchedulingEngageTask, resetSchedulingControls } =
    useEngageTaskScheduling()

  const onSubmit = useCallback(
    async (values) => {
      // before a new offboard/schedule reset data from all controls so we can render a clean submit
      resetOffboardUserControls()
      resetSchedulingControls()

      const parameters = toApiPayload({ values, employee, userInfo })

      if (isScheduleSelected(values)) {
        await scheduleEngageTask({
          customerId,
          payload: {
            target: employee.id,
            created_by: userId,
            action: SCHEDULER_ACTIONS.OFFBOARDING,
            // meta_data should match the request payload of the hub task
            meta_data: {
              customerId,
              organizationId,
              payload: parameters,
            },
            ...toScheduleApiPayload(values), // schedule form to api values (start_time, ...)
          },
        })
        fetchUpcomingEngageTasks()
      } else {
        await offboardUser({
          customerId,
          organizationId,
          userId,
          parameters,
        })

        onUpdate()
      }
    },
    [
      offboardUser,
      employee,
      userInfo,
      customerId,
      organizationId,
      userId,
      onUpdate,
      scheduleEngageTask,
      resetSchedulingControls,
      resetOffboardUserControls,
      fetchUpcomingEngageTasks,
    ],
  )

  const getLoadingDescription = useCallback((values, isSubmitting) => {
    if (isSubmitting && isScheduleSelected(values)) {
      return 'ENGAGE.SCHEDULING.SCHEDULING_IN_PROGRESS'
    }

    if (isSubmitting) {
      return 'ENGAGE.OFFBOARDING_USER'
    }

    return null
  }, [])

  return (
    <FormOptionsContext.Provider value={formOptions}>
      <Formik
        initialValues={initialValues}
        enableReinitialize // enableReinitialize flag forces the form to reset every time initial values change, thus initialValues has to be memoized correctly
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, dirty, isSubmitting }) => (
          <ModalSidebar
            show={show}
            onHide={onHide}
            shouldShowCloseConfirmation={() => dirty && !(successOffboardingUser || successSchedulingEngageTask)}
            steps={MODAL_STEPS}
            initialStepKey={STEP_KEYS.DELETE_USER}
            title="ENGAGE.OFFBOARD.OFFBOARD_USER"
            subTitles={[employeeFullName, intl.formatMessage({ id: 'ENGAGE.MICROSOFT_365' })]}
            variant="engage"
            isLoading={isSubmitting || isFetchingUserInfo}
            loadingDescription={getLoadingDescription(values, isSubmitting)}
            toBackdrop={toBackdrop}
          >
            <ModalPanelSwitch stepKey={STEP_KEYS.DELETE_USER}>
              <DeleteUserPanel
                employeeFullName={employeeFullName}
                employeeEmailOptions={employeeEmailOptions}
                userInfo={userInfo}
                isUserMailBoxAvailable={isUserMailBoxAvailable}
                scheduledTask={scheduledTask}
              />
            </ModalPanelSwitch>

            <ModalPanelSwitch stepKey={STEP_KEYS.OPTIONAL_SETTINGS}>
              <OptionalSettingsPanel />
            </ModalPanelSwitch>

            <ModalPanelSwitch stepKey={STEP_KEYS.REVIEW}>
              <ReviewPanel employeeFullName={employeeFullName} userInfo={userInfo} />
            </ModalPanelSwitch>

            <ModalPanelSwitch stepKey={STEP_KEYS.SUBMIT}>
              <SubmitPanel
                employeeFullName={employeeFullName}
                userInfo={userInfo}
                submissionError={errorOffboardingUser}
                successWarnings={offboardingUserData?.warnings}
                errorSchedulingEngageTask={errorSchedulingEngageTask}
              />
            </ModalPanelSwitch>
          </ModalSidebar>
        )}
      </Formik>
    </FormOptionsContext.Provider>
  )
}

OffboardModal.propTypes = propTypes

export default OffboardModal
